import React, { useEffect } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const LinkedinCallback: React.FC = () => {
  const { t } = useTranslation('pages');

  useEffect(() => {
    const notifyOpener = async () => {
      // hoist these so the finally block can reference them safely
      let payload: any = null;
      let profile: any = null;
      let error: any = null;
      try {
        // Call public session-profile endpoint on backend
        // Add Cache-Control and a timestamp query param to avoid intermediate
        // caches returning 304 Not Modified (some CDNs/proxies may cache by URL).
  const res = await api.post('/auth/linkedin/session-profile', {}, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { t: Date.now() }
        });
        payload = res.data?.data || {};
        profile = payload.profile || null;
        error = payload.error || null;

  if (window.opener) {
          // Normalize profile keys to support different shapes and ensure
          // the opener can read predictable properties.
          const normalizeProfile = (p: any) => {
            if (!p) return null;
            return {
              // common variants
              firstName: p.firstName || p.given_name || p.first_name || null,
              lastName: p.lastName || p.family_name || p.last_name || null,
              email: p.email || p.emailAddress || p.mail || null,
              // keep original payload for debugging/extra fields
              _raw: p
            };
          };

          const normalized = profile ? normalizeProfile(profile) : null;

          // Choose a safe targetOrigin: try to use opener.location.origin if accessible
          // Use current window origin as fallback instead of '*' for security
          let targetOrigin = window.location.origin;
          try {
            const openerOrigin = window.opener && window.opener.location && window.opener.location.origin;
            if (openerOrigin) {
              targetOrigin = openerOrigin;
            }
          } catch (e) {
            // Accessing opener.location may throw cross-origin; use current origin as fallback
            // eslint-disable-next-line no-console
            console.debug('Could not read opener.location.origin, using current origin as targetOrigin', e);
          }

          const doPost = (msg: any) => {
            try {
              // eslint-disable-next-line no-console
              console.debug('Posting message to opener', { targetOrigin, msg });
              window.opener.postMessage(msg, targetOrigin);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('Failed to postMessage to opener', e);
            }
          };

          // If backend asked frontend to redirect (for example to /logout), honor it.
          const redirectTo = payload?.redirectTo || null;
          if (redirectTo) {
            try {
              // Try to navigate the opener if same-origin
              try {
                window.opener.location.href = redirectTo;
              } catch {
                // If cross-origin, fallback to postMessage so opener can handle navigation
                const redirectMsg = { type: 'linkedin_redirect', data: { redirectTo } };
                try {
                  // Use current origin or try to get opener origin safely
                  const safeOrigin = window.location.origin;
                  window.opener.postMessage(redirectMsg, safeOrigin);
                } catch { /* ignore */ }
              }
            } catch {
              // ignore
            }
          }

          if (normalized) {
            // Post multiple times to handle cases where the opener listener isn't ready yet
            const msg = { type: 'linkedin_profile', data: normalized };
            doPost(msg);
            setTimeout(() => doPost(msg), 200);
            setTimeout(() => doPost(msg), 500);

            // Fallback: write to localStorage so opener can pick it up via "storage" event
            try {
              localStorage.setItem('linkedin_profile_popup', JSON.stringify({ data: normalized, ts: Date.now() }));
              // schedule removal shortly after to avoid stale data
              setTimeout(() => { try { localStorage.removeItem('linkedin_profile_popup'); } catch { /* ignore */ } }, 5000);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.debug('Could not write linkedin_profile_popup to localStorage', e);
            }
            // Notify opener to refresh server-side profile data
            try {
              const refreshMsg = { type: 'linkedin_refresh' };
              doPost(refreshMsg);
              setTimeout(() => doPost(refreshMsg), 200);
              try {
                localStorage.setItem('linkedin_profile_popup_refresh', JSON.stringify({ ts: Date.now() }));
                setTimeout(() => { try { localStorage.removeItem('linkedin_profile_popup_refresh'); } catch { /* ignore */ } }, 5000);
              } catch { /* ignore */ }
            } catch {
              // ignore
            }
          } else if (error) {
            const msg = { type: 'linkedin_error', data: error };
            doPost(msg);
            setTimeout(() => doPost(msg), 200);
            try {
              localStorage.setItem('linkedin_profile_popup_error', JSON.stringify({ data: error, ts: Date.now() }));
              setTimeout(() => { try { localStorage.removeItem('linkedin_profile_popup_error'); } catch { /* ignore */ } }, 5000);
            } catch { /* ignore */ }
          } else {
            const msg = { type: 'linkedin_error', data: { message: 'No profile or error in session' } };
            doPost(msg);
            setTimeout(() => doPost(msg), 200);
            try {
              localStorage.setItem('linkedin_profile_popup_error', JSON.stringify({ data: { message: 'No profile or error in session' }, ts: Date.now() }));
              setTimeout(() => { try { localStorage.removeItem('linkedin_profile_popup_error'); } catch { /* ignore */ } }, 5000);
            } catch { /* ignore */ }
          }
        }
      } catch (e: any) {
        console.error('Error fetching linkedin session profile', e);
        try {
          if (window.opener) {
            const msg = { type: 'linkedin_error', data: { message: e?.message || 'Unknown error' } };
            try {
              // Use current origin instead of '*' for security
              window.opener.postMessage(msg, window.location.origin);
            } catch (err) { console.error('postMessage failed', err); }
          }
        } catch (e2) {
          console.error(e2);
        }
      } finally {
        // Close popup after a longer delay (5s) to give you time to inspect console
        // in case debugging is needed. Keep visible logs for debugging.
        // NOTE: This is temporary and can be reduced after debugging.
        try {
          // eslint-disable-next-line no-console
          console.log('LinkedIn callback popup finished; will close in 5s. Session payload:', { payload, profile, error });
        } catch { /* ignore */ }
        setTimeout(() => {
          try { window.close(); } catch (e) { console.error(e); }
        }, 5000);
      }
    };

    notifyOpener();
  }, []);

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', textAlign: 'center', padding: 40 }}>
      <h2>{t('linkedinCallback.title')}</h2>
      <p>{t('linkedinCallback.subtitle')}</p>
    </div>
  );
};

export default LinkedinCallback;
